import { zodResolver } from '@hookform/resolvers/zod'
import { useForm } from 'react-hook-form'
import { Link, useNavigate, useSearchParams } from 'react-router-dom'
import { useRegistrarOs } from '../../../api/queries/ordensDeServico'
import { useSessao } from '../../../auth/contexto'
import { Botao, BotaoLink } from '../../../components/Botao'
import { AvisoDeErro, EstadoVazio } from '../../../components/Estados'
import { IconeSeta } from '../../../components/Icones'
import { podeEscreverOs } from '../acessoDaOs'
import { OS } from '../textosDaOs'
import { CamposDaIdentificacao } from './CamposDaIdentificacao'
import { CamposDosItens } from './CamposDosItens'
import { FORMULARIO } from './textosDoFormulario'
import { esquemaDeOs, paraRequisicao, type FormularioDeOs } from './esquema'

/**
 * O registro de uma ordem de serviço — o formulário inteiro, num POST só.
 *
 * **Não há rascunho, e é decisão.** A ordem já existe assinada quando chega aqui (foi assim com a
 * `001-SUST-MAI-2026`): o sistema registra um fato, não compõe um documento. Isso é o que fez a
 * tabela ter uma única exceção de trigger — a revogação — e nenhum `DELETE` em lugar nenhum. No
 * sistema anterior, 16 ordens foram apagadas fisicamente e 83 linhas de valor foram junto.
 *
 * **O mês vem na URL quando a faixa de meses descobertos manda para cá.** O formulário tem vinte e
 * quatro campos, e fazer a pessoa reescrever o único que a faixa acabou de dizer é como se digita
 * 2026-07 no mês de agosto.
 *
 * Esconder o item no menu não bastaria: este endereço é colável. Quem decide continua sendo o
 * servidor (`@PreAuthorize(FISCAL)`); a recusa aqui evita meia hora de preenchimento para receber
 * um 403 no envio.
 *
 * @returns a tela de registro
 */
export function PaginaRegistrarOs() {
  const [parametros] = useSearchParams()
  const navegar = useNavigate()
  const { usuario } = useSessao()
  const registrar = useRegistrarOs()

  const competencia = parametros.get('competencia') ?? ''

  const {
    register,
    control,
    handleSubmit,
    watch,
    formState: { errors, isSubmitting },
  } = useForm<FormularioDeOs>({
    resolver: zodResolver(esquemaDeOs),
    defaultValues: {
      regime: 'SUSTENTACAO_INFRAESTRUTURA',
      tipo: '',
      competencia: /^\d{4}-\d{2}$/.test(competencia) ? competencia : '',
      itens: [
        {
          item: '1',
          descricao: '',
          metrica: 'VALOR_FIXO',
          unidade: '',
          catser: '',
          valorUnitario: '',
          quantidadeNumerador: 1,
          quantidadeDenominador: 1,
          valorTotalDeclarado: '',
        },
      ],
    },
  })

  if (!podeEscreverOs(usuario)) {
    return (
      <EstadoVazio
        titulo={FORMULARIO.semAcesso.titulo}
        ajuda={FORMULARIO.semAcesso.ajuda}
        acao={
          <BotaoLink to="/ordens-servico">{OS.voltarParaALista}</BotaoLink>
        }
      />
    )
  }

  return (
    <form
      className="max-w-5xl space-y-4"
      onSubmit={handleSubmit((dados) =>
        registrar.mutateAsync(paraRequisicao(dados)).then((ordem) => navegar(`/ordens-servico/${ordem.id}`)),
      )}
    >
      <Link
        to="/ordens-servico"
        className="inline-flex items-center gap-1.5 text-sm text-niteroi-700 underline underline-offset-2"
      >
        <IconeSeta className="h-4 w-4 rotate-180" />
        {OS.voltarParaALista}
      </Link>

      <header>
        <h1 className="titulo-da-pagina">{FORMULARIO.titulo}</h1>
        <p className="mt-1 max-w-prose text-sm text-slate-600">{FORMULARIO.ajuda}</p>
        <p className="mt-1 max-w-prose text-sm text-slate-600">{FORMULARIO.semRascunho}</p>
      </header>

      <CamposDaIdentificacao register={register} errors={errors} regime={watch('regime')} />
      <CamposDosItens control={control} register={register} errors={errors} />

      {registrar.isError && <AvisoDeErro erro={registrar.error} titulo="A ordem não foi registrada" />}

      <div className="flex flex-wrap gap-2">
        <Botao type="submit" aparencia="primario" disabled={isSubmitting || registrar.isPending}>
          {FORMULARIO.enviar}
        </Botao>
        <BotaoLink to="/ordens-servico">{FORMULARIO.cancelar}</BotaoLink>
      </div>
    </form>
  )
}
